import type { AppLocale, ThemePreference } from '@ielts-buddy/shared'
import { useCallback, useEffect, useState } from 'react'
import { t } from '../i18n/messages.js'
import { ApiError } from '../shared/api.js'
import { useAuthStore } from '../shared/auth-store.js'
import { usePreferencesStore } from '../shared/preferences-store.js'
import { useRouter } from '../shared/router-store.js'
import { applyAppTheme, haptics } from '../shared/telegram.js'
import { CheckIcon, ChevronRightIcon } from '../shared/ui/icons.js'
import { Avatar, Button } from '../shared/ui/index.js'

const LOCALES: { id: AppLocale; labelKey: string }[] = [
  { id: 'en', labelKey: 'profile.lang.en' },
  { id: 'ru', labelKey: 'profile.lang.ru' },
  { id: 'uz', labelKey: 'profile.lang.uz' },
]

const THEMES: { id: ThemePreference; labelKey: string }[] = [
  { id: 'match_telegram', labelKey: 'profile.theme.match' },
  { id: 'light', labelKey: 'profile.theme.light' },
  { id: 'dark', labelKey: 'profile.theme.dark' },
]

type OpenSection = 'theme' | 'language' | null

export function ProfileScreen() {
  const user = useAuthStore((s) => s.user)
  const deleteAccount = useAuthStore((s) => s.deleteAccount)
  const syncProfileFromTelegram = useAuthStore((s) => s.syncProfileFromTelegram)
  const navigateHome = useRouter((s) => s.navigateHome)
  const locale = usePreferencesStore((s) => s.locale)
  const setLocale = usePreferencesStore((s) => s.setLocale)
  const themePreference = usePreferencesStore((s) => s.themePreference)
  const setThemePreference = usePreferencesStore((s) => s.setThemePreference)

  const [openSection, setOpenSection] = useState<OpenSection>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [photoSynced, setPhotoSynced] = useState(false)

  const firstName = user?.firstName ?? 'there'
  const initial = (firstName.trim()[0] ?? '?').toUpperCase()

  const themeLabel = t(
    locale,
    THEMES.find((x) => x.id === themePreference)?.labelKey ??
      'profile.theme.match',
  )
  const localeLabel = t(
    locale,
    LOCALES.find((x) => x.id === locale)?.labelKey ?? 'profile.lang.en',
  )

  const toggle = (section: Exclude<OpenSection, null>) => {
    haptics.select()
    setOpenSection((cur) => (cur === section ? null : section))
  }

  const closeDelete = useCallback(() => {
    setDeleteOpen(false)
    setDeleteError(null)
  }, [])

  useEffect(() => {
    if (!photoSynced) return
    const tmr = window.setTimeout(() => setPhotoSynced(false), 2800)
    return () => window.clearTimeout(tmr)
  }, [photoSynced])

  const onSyncPhoto = useCallback(async () => {
    setPhotoError(null)
    setPhotoSynced(false)
    setPhotoBusy(true)
    haptics.light()
    try {
      await syncProfileFromTelegram()
      setPhotoSynced(true)
      haptics.success()
    } catch (e) {
      setPhotoError(
        e instanceof ApiError ? e.message : t(locale, 'profile.syncPhotoError'),
      )
      haptics.error()
    } finally {
      setPhotoBusy(false)
    }
  }, [locale, syncProfileFromTelegram])

  const confirmDelete = useCallback(async () => {
    setDeleteError(null)
    setDeleteBusy(true)
    try {
      navigateHome()
      await deleteAccount()
      closeDelete()
    } catch (e) {
      setDeleteError(
        e instanceof ApiError ? e.message : t(locale, 'profile.deleteFailed'),
      )
    } finally {
      setDeleteBusy(false)
    }
  }, [closeDelete, deleteAccount, locale, navigateHome])

  return (
    <div className="min-h-full flex flex-col animate-fade-in pb-2 bg-brand-bg">
      <header className="screen-padding pt-3 pb-2">
        <div className="flex items-center gap-4 min-h-[3rem]">
          <Avatar
            photoUrl={user?.photoUrl ?? null}
            initial={initial}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-brand-text-muted">
              {t(locale, 'profile.title')}
            </p>
            <h1 className="text-[22px] font-semibold text-brand-text leading-tight tracking-tight truncate">
              {firstName}
            </h1>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col gap-0 px-3">
        <p className="text-[13px] font-medium text-brand-text-muted px-3 pt-4 pb-1">
          {t(locale, 'profile.section.display')}
        </p>
        <div className="rounded-xl bg-brand-surface overflow-hidden border border-brand-settings-outline shadow-sm">
          <button
            type="button"
            className="w-full flex items-center min-h-[48px] pl-4 pr-3 gap-3 text-left active:bg-brand-bg-soft/90 transition-colors"
            aria-expanded={openSection === 'theme'}
            onClick={() => toggle('theme')}
          >
            <span className="flex-1 min-w-0 text-[17px] leading-snug text-brand-text">
              {t(locale, 'profile.theme')}
            </span>
            <span className="text-[17px] text-brand-text-muted truncate max-w-[45%]">
              {themeLabel}
            </span>
            <ChevronRightIcon
              className={`w-[22px] h-[22px] shrink-0 text-brand-text-subtle transition-transform duration-200 ${
                openSection === 'theme' ? 'rotate-90' : ''
              }`}
            />
          </button>
          {openSection === 'theme' && (
            <div className="border-t border-brand-list-divider bg-brand-bg-soft/35">
              {THEMES.map(({ id, labelKey }, idx) => {
                const selected = themePreference === id
                return (
                  <button
                    key={id}
                    type="button"
                    className={`w-full flex items-center min-h-[44px] pl-4 pr-3 gap-3 text-left text-[17px] transition-colors ${
                      idx > 0 ? 'border-t border-brand-list-divider' : ''
                    } ${
                      selected
                        ? 'bg-brand-primary-soft/60 text-brand-primary-strong'
                        : 'text-brand-text active:bg-brand-bg-soft'
                    }`}
                    onClick={() => {
                      haptics.light()
                      setThemePreference(id)
                      applyAppTheme(id)
                      setOpenSection(null)
                    }}
                  >
                    <span className="w-7 flex justify-center shrink-0">
                      {selected ? (
                        <CheckIcon className="w-5 h-5 text-brand-primary-strong" />
                      ) : null}
                    </span>
                    <span className="flex-1">{t(locale, labelKey)}</span>
                  </button>
                )
              })}
            </div>
          )}

          <div className="h-px bg-brand-list-divider ml-4" aria-hidden />

          <button
            type="button"
            className="w-full flex items-center min-h-[48px] pl-4 pr-3 gap-3 text-left active:bg-brand-bg-soft/90 transition-colors"
            aria-expanded={openSection === 'language'}
            onClick={() => toggle('language')}
          >
            <span className="flex-1 min-w-0 text-[17px] leading-snug text-brand-text">
              {t(locale, 'profile.language')}
            </span>
            <span className="text-[17px] text-brand-text-muted truncate max-w-[45%]">
              {localeLabel}
            </span>
            <ChevronRightIcon
              className={`w-[22px] h-[22px] shrink-0 text-brand-text-subtle transition-transform duration-200 ${
                openSection === 'language' ? 'rotate-90' : ''
              }`}
            />
          </button>
          {openSection === 'language' && (
            <div className="border-t border-brand-list-divider bg-brand-bg-soft/35">
              {LOCALES.map(({ id, labelKey }, idx) => {
                const selected = locale === id
                return (
                  <button
                    key={id}
                    type="button"
                    className={`w-full flex items-center min-h-[44px] pl-4 pr-3 gap-3 text-left text-[17px] transition-colors ${
                      idx > 0 ? 'border-t border-brand-list-divider' : ''
                    } ${
                      selected
                        ? 'bg-brand-primary-soft/60 text-brand-primary-strong'
                        : 'text-brand-text active:bg-brand-bg-soft'
                    }`}
                    onClick={() => {
                      haptics.light()
                      setLocale(id)
                      setOpenSection(null)
                    }}
                  >
                    <span className="w-7 flex justify-center shrink-0">
                      {selected ? (
                        <CheckIcon className="w-5 h-5 text-brand-primary-strong" />
                      ) : null}
                    </span>
                    <span className="flex-1">{t(locale, labelKey)}</span>
                  </button>
                )
              })}
            </div>
          )}

          <div className="h-px bg-brand-list-divider ml-4" aria-hidden />

          <div className="px-4 py-3 flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-[17px] font-medium text-brand-text">
                {t(locale, 'profile.updatePhoto')}
              </span>
              <span className="text-[13px] leading-snug text-brand-text-muted">
                {t(locale, 'profile.updatePhotoHint')}
              </span>
            </div>
            <Button
              variant="secondary"
              type="button"
              className="w-full sm:w-auto self-start"
              haptic={false}
              disabled={photoBusy}
              onClick={() => void onSyncPhoto()}
            >
              {photoBusy
                ? t(locale, 'profile.syncing')
                : t(locale, 'profile.updatePhotoAction')}
            </Button>
            {photoError ? (
              <p className="text-[13px] text-brand-error">{photoError}</p>
            ) : null}
            {photoSynced ? (
              <p className="text-[13px] text-brand-primary-strong font-medium">
                {t(locale, 'profile.synced')}
              </p>
            ) : null}
          </div>
        </div>

        <p className="text-[13px] font-medium text-brand-text-muted px-3 pt-6 pb-1">
          {t(locale, 'profile.section.account')}
        </p>
        <div className="rounded-xl bg-brand-surface overflow-hidden border border-brand-settings-outline shadow-sm">
          <button
            type="button"
            className="w-full min-h-[48px] px-4 text-left text-[17px] text-brand-error font-medium active:bg-brand-error-soft/50 transition-colors"
            onClick={() => {
              haptics.light()
              setDeleteError(null)
              setDeleteOpen(true)
            }}
          >
            {t(locale, 'profile.deleteAccount')}
          </button>
        </div>
      </div>

      {deleteOpen ? (
        // biome-ignore lint/a11y/noStaticElementInteractions: full-screen modal backdrop; cannot use <button> (invalid nesting with dialog content).
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-5 bg-black/45 backdrop-blur-[3px]"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteBusy) closeDelete()
          }}
        >
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: only stops mouse bubbling to backdrop; dialog is keyboard-operable via inner buttons */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            className="w-full max-w-[320px] rounded-2xl bg-brand-surface border border-brand-settings-outline shadow-brand-lg p-5 flex flex-col gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="delete-account-title"
              className="text-lg font-semibold text-brand-text text-center"
            >
              {t(locale, 'profile.deleteConfirmTitle')}
            </h2>
            <p className="text-sm text-brand-text-muted text-center leading-relaxed">
              {t(locale, 'profile.deleteConfirmBody')}
            </p>
            {deleteError ? (
              <p className="text-sm text-brand-error text-center">
                {deleteError}
              </p>
            ) : null}
            <div className="flex gap-2 mt-1">
              <Button
                variant="secondary"
                type="button"
                className="flex-1"
                haptic={false}
                disabled={deleteBusy}
                onClick={() => {
                  if (!deleteBusy) closeDelete()
                }}
              >
                {t(locale, 'profile.cancel')}
              </Button>
              <Button
                variant="secondary"
                type="button"
                className="flex-1 border-brand-error/40 text-brand-error font-semibold"
                haptic={false}
                disabled={deleteBusy}
                onClick={() => void confirmDelete()}
              >
                {deleteBusy
                  ? t(locale, 'profile.deleting')
                  : t(locale, 'profile.deleteConfirm')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
